import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Testimonial, Project } from '../../types';
import { db } from '../../firebase';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';
import { GoogleGenAI } from '@google/genai';
import { useCollection } from '../../hooks/useCollection';

const SparklesIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8-5.8 1.9 5.8 1.9 1.9 5.8 1.9-5.8 5.8-1.9-5.8-1.9z"/></svg>;

const AdminTestimonialFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    
    const { data: projects } = useCollection<Project>('projects', { field: 'title', direction: 'asc' });

    const [testimonial, setTestimonial] = useState<Omit<Testimonial, 'id'>>({
        clientName: '',
        companyName: '',
        quote: '',
        projectSlug: '',
        order: 0,
    });

    useEffect(() => {
        const fetchData = async () => {
            if (isEditing && id) {
                const docRef = doc(db, 'testimonials', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setTestimonial(docSnap.data() as Testimonial);
                } else {
                    showToast('Testimonial not found.', { type: 'error' });
                    navigate('/admin/testimonials');
                }
            } else {
                const snapshot = await getDocs(collection(db, 'testimonials'));
                setTestimonial(prev => ({ ...prev, order: snapshot.size }));
            }
            setIsLoading(false);
        };
        fetchData();
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setTestimonial(prev => ({ ...prev, [name]: value }));
    };
    
    const handleGenerateQuote = async () => {
        if (!testimonial.quote.trim()) {
            showToast("Please enter some text to improve.", { type: 'error' });
            return;
        }
        setIsGenerating(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const prompt = `As a professional corporate copywriter, rewrite the following client testimonial to be more impactful and polished, while preserving the original sentiment. The original quote is: "${testimonial.quote}". Return only the improved quote text, without any quotation marks around it.`;
            const response = await ai.models.generateContent({ model: 'gemini-3-flash-preview', contents: prompt });
            const text = response.text.trim();
            if (text) {
                setTestimonial(prev => ({ ...prev, quote: text }));
                showToast('Quote improved with AI!', { type: 'success' });
            }
        } catch(e) {
            showToast('Failed to generate quote.', { type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const dataToSave = { ...testimonial };
            if (isEditing && id) {
                await updateDoc(doc(db, 'testimonials', id), dataToSave);
                showToast('Testimonial updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'testimonials'), dataToSave);
                showToast('Testimonial added successfully!', { type: 'success' });
            }
            navigate('/admin/testimonials');
        } catch (error) {
            showToast('Failed to save testimonial.', { type: 'error' });
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="text-center p-8">Loading...</div>;

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Testimonial' : 'Add New Testimonial'}</h2>
            <form onSubmit={handleSubmit}>
                <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto space-y-6">
                    <div>
                        <label htmlFor="clientName" className="block text-sm font-medium text-gray-700">Client Name</label>
                        <input type="text" name="clientName" id="clientName" value={testimonial.clientName} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                    </div>
                    <div>
                        <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name (Optional)</label>
                        <input type="text" name="companyName" id="companyName" value={testimonial.companyName || ''} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                    </div>
                    <div>
                        <label htmlFor="quote" className="block text-sm font-medium text-gray-700">Quote</label>
                        <div className="relative">
                            <textarea name="quote" id="quote" rows={5} value={testimonial.quote} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
                            <button type="button" onClick={handleGenerateQuote} disabled={isGenerating} className="absolute top-2.5 right-1.5 p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-50" title="Improve with AI">
                                <SparklesIcon className={`h-5 w-5 ${isGenerating ? 'animate-pulse text-indigo-600' : ''}`} />
                            </button>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="projectSlug" className="block text-sm font-medium text-gray-700">Link to Project (Optional)</label>
                        <select name="projectSlug" id="projectSlug" value={testimonial.projectSlug || ''} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md">
                            <option value="">None</option>
                            {projects?.map(project => (
                                <option key={project.id} value={project.slug}>{project.title}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end space-x-4 pt-4">
                        <button type="button" onClick={() => navigate('/admin/testimonials')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                            {isSaving ? 'Saving...' : 'Save Testimonial'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdminTestimonialFormPage;